const CONSENT_API_POLL_MS = 50;
const CONSENT_API_TIMEOUT_MS = 3000;
const GTM_TRIGGER_EVENT = 'gtm.triggerGroup';
const CONSENT_COLLECTED_EVENT = 'visitorConsentCollected';
const CONSENT_CACHE_KEY = 'shopifyConsentCacheV1';

// Lets header-scripts.liquid replay this on the next page load, before
// Shopify's Privacy API has re-initialized (see syncStoredConsentEarly there).
function cacheConsentState(consentValues, googleStates) {
  try {
    window.localStorage.setItem(
      CONSENT_CACHE_KEY,
      JSON.stringify({ consentValues, googleStates })
    );
  } catch (error) {
    // Ignore storage failures.
  }
}

/**
 * Maps Shopify Customer Privacy API consent to Google Consent Mode v2 states.
 * Mirrors the GTM "Customer Privacy API" community template mapping.
 */
function mapShopifyConsentToGoogleCM(consent) {
  const marketing = Boolean(consent?.marketingAllowed);
  const analytics = Boolean(consent?.analyticsAllowed);
  const preferences = Boolean(consent?.preferencesAllowed);

  return {
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
    analytics_storage: analytics ? 'granted' : 'denied',
    functionality_storage: preferences ? 'granted' : 'denied',
    personalization_storage: preferences ? 'granted' : 'denied',
    security_storage: 'granted',
  };
}

function gtagConsentUpdate(states) {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  window.gtag('consent', 'update', states);
}

function sanitizeShopifyConsentDetail(detail) {
  const response = { ...(detail || {}) };
  delete response.firstPartyMarketingAllowed;
  delete response.thirdPartyMarketingAllowed;
  delete response.saleOfDataAllowed;
  return response;
}

function usesCustomCaliforniaPopup() {
  return Boolean(document.querySelector('california-privacy-popup'));
}

function buildConsentPayload(consent) {
  return {
    marketingAllowed: Boolean(consent?.marketingAllowed ?? consent?.marketing),
    analyticsAllowed: Boolean(consent?.analyticsAllowed ?? consent?.analytics),
    preferencesAllowed: Boolean(consent?.preferencesAllowed ?? consent?.preferences),
  };
}

function pushPendingConsentToDataLayer() {
  const consent = buildConsentPayload({
    marketingAllowed: false,
    analyticsAllowed: false,
    preferencesAllowed: false,
  });

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    shopify_consent: consent,
    ...consent,
  });
}

function pushConsentToDataLayer(consent, eventName) {
  window.dataLayer = window.dataLayer || [];
  const consentValues = buildConsentPayload(consent);
  const payload = {
    shopify_consent: sanitizeShopifyConsentDetail(consent),
    ...consentValues,
  };

  if (eventName) {
    payload.event = eventName;
  }

  window.dataLayer.push(payload);
}

function readStoredVisitorConsent(privacy) {
  if (!privacy || typeof privacy.currentVisitorConsent !== 'function') return null;

  const stored = privacy.currentVisitorConsent();
  if (!stored) return null;

  const toBool = (value) => value === 'yes' || value === true;

  return {
    marketingAllowed: toBool(stored.marketing),
    analyticsAllowed: toBool(stored.analytics),
    preferencesAllowed: toBool(stored.preferences),
    saleOfDataAllowed: toBool(stored.sale_of_data),
  };
}

function hasExplicitStoredConsent(privacy) {
  if (!privacy || typeof privacy.currentVisitorConsent !== 'function') return false;

  const stored = privacy.currentVisitorConsent();
  if (!stored) return false;

  return ['marketing', 'analytics', 'preferences', 'sale_of_data'].some((key) => {
    const value = stored[key];
    return value === 'yes' || value === 'no' || value === true || value === false;
  });
}

function updateGoogleConsentMode(consent) {
  // Normalize first: mapShopifyConsentToGoogleCM only reads *Allowed keys, but
  // the popup's own consent object (marketing/analytics/preferences) doesn't
  // use that shape - without this, live consent updates would map to all-denied.
  const normalizedConsent = buildConsentPayload(consent);
  const states = mapShopifyConsentToGoogleCM(normalizedConsent);
  gtagConsentUpdate(states);
  cacheConsentState(normalizedConsent, states);
  pushConsentToDataLayer(consent, GTM_TRIGGER_EVENT);
}

function onUserConsent(consent) {
  updateGoogleConsentMode(consent);
}

function waitForCustomerPrivacy() {
  return new Promise((resolve) => {
    if (window.Shopify?.customerPrivacy) {
      resolve(window.Shopify.customerPrivacy);
      return;
    }

    const interval = window.setInterval(() => {
      if (window.Shopify?.customerPrivacy) {
        window.clearInterval(interval);
        resolve(window.Shopify.customerPrivacy);
      }
    }, CONSENT_API_POLL_MS);

    window.setTimeout(() => {
      window.clearInterval(interval);
      resolve(window.Shopify?.customerPrivacy || null);
    }, CONSENT_API_TIMEOUT_MS);
  });
}

function initCustomerPrivacyConsent() {
  waitForCustomerPrivacy().then((privacy) => {
    if (!privacy) return;

    window.gtmOnUserConsent = onUserConsent;

    const isCustomPopup = usesCustomCaliforniaPopup();
    const shouldShowBanner =
      typeof privacy.shouldShowBanner === 'function' ? privacy.shouldShowBanner() : false;
    const hasStoredConsent = hasExplicitStoredConsent(privacy);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      BannerActive: shouldShowBanner,
    });

    // Loading time only: push all false before user interacts with popup.
    if (isCustomPopup && !hasStoredConsent) {
      pushPendingConsentToDataLayer();
    } else if (!isCustomPopup && shouldShowBanner) {
      pushPendingConsentToDataLayer();
    } else if (hasStoredConsent) {
      updateGoogleConsentMode(readStoredVisitorConsent(privacy));
    }

    document.addEventListener(CONSENT_COLLECTED_EVENT, (event) => {
      const detail = event.detail || {};
      updateGoogleConsentMode(detail);
    });
  });
}

initCustomerPrivacyConsent();
